import { useEffect, useMemo, useRef, useState } from 'react'
import { Send, Zap, Check, Twitter, MessageCircle, ChevronRight, ChevronDown, Loader2, Globe, Layers } from 'lucide-react'
import { apiClient } from '../utils/api'

function SiteGen2() {
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [currentStep, setCurrentStep] = useState('tokenName')
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState('')
  const [previewHtml, setPreviewHtml] = useState('')

  const [data, setData] = useState({
    tokenName: '',
    tokenSymbol: '',
    contractAddress: '',
    description: '',
    socialLinks: { twitter: '', telegram: '' },
    dexPlatforms: { pumpswap: false, pancakeswap: false, raydium: false, jupiter: false, dexscreener: false },
  })

  const chatEndRef = useRef(null)

  useEffect(() => {
    setMessages([
      { id: 'm1', role: 'assistant', content: 'Welcome! Let’s set up your memecoin site. What is your token name?' },
    ])
  }, [])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, previewHtml])

  const canSubmit = useMemo(() => {
    if (isGenerating) return false
    if (currentStep === 'tokenName') return inputValue.trim().length > 0
    if (currentStep === 'tokenSymbol') return inputValue.trim().length > 0
    if (currentStep === 'contractAddress') return inputValue.trim().length > 0
    return true
  }, [currentStep, inputValue, isGenerating])

  const handleSubmit = () => {
    if (!canSubmit) return
    setError('')

    if (currentStep === 'tokenName') {
      const tokenName = inputValue.trim()
      setData(prev => ({ ...prev, tokenName }))
      setMessages(prev => [
        ...prev,
        { id: crypto.randomUUID(), role: 'user', content: tokenName },
        { id: crypto.randomUUID(), role: 'assistant', content: 'Great! What is your token symbol?' },
      ])
      setInputValue('')
      setCurrentStep('tokenSymbol')
      return
    }

    if (currentStep === 'tokenSymbol') {
      const tokenSymbol = inputValue.trim().replace(/^\$/,'').toUpperCase()
      setData(prev => ({ ...prev, tokenSymbol }))
      setMessages(prev => [
        ...prev,
        { id: crypto.randomUUID(), role: 'user', content: `$${tokenSymbol}` },
        { id: crypto.randomUUID(), role: 'assistant', content: 'Got it. Please paste your contract address.' },
      ])
      setInputValue('')
      setCurrentStep('contractAddress')
      return
    }

    if (currentStep === 'contractAddress') {
      const contractAddress = inputValue.trim()
      setData(prev => ({ ...prev, contractAddress }))
      setMessages(prev => [
        ...prev,
        { id: crypto.randomUUID(), role: 'user', content: contractAddress },
        { id: crypto.randomUUID(), role: 'assistant', content: 'Would you like to add socials? You can enter Twitter and Telegram below.' , type: 'socials'},
      ])
      setInputValue('')
      setCurrentStep('socials')
      return
    }
  }

  const handleSocialsContinue = (values) => {
    setData(prev => ({ ...prev, socialLinks: { twitter: values.twitter, telegram: values.telegram } }))
    setMessages(prev => [
      ...prev,
      { id: crypto.randomUUID(), role: 'assistant', content: 'Select your DEX platforms below.' , type: 'dex'},
    ])
    setCurrentStep('dex')
  }

  const handleDexContinue = (values) => {
    setData(prev => ({ ...prev, dexPlatforms: values }))
    setMessages(prev => [
      ...prev,
      { id: crypto.randomUUID(), role: 'assistant', content: 'Review your info and generate a preview.', type: 'confirm' },
    ])
    setCurrentStep('confirm')
  }

  const generatePreview = async () => {
    setIsGenerating(true)
    setError('')
    setPreviewHtml('')

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('tokenName', data.tokenName)
      formDataToSend.append('tokenSymbol', data.tokenSymbol)
      formDataToSend.append('contractAddress', data.contractAddress)
      formDataToSend.append('description', data.description)
      formDataToSend.append('socialLinks', JSON.stringify({ twitter: data.socialLinks.twitter, telegram: data.socialLinks.telegram }))
      formDataToSend.append('dexPlatforms', JSON.stringify(data.dexPlatforms))

      const response = await apiClient.generatePreview(formDataToSend)
      setPreviewHtml(response.html)
      setMessages(prev => [
        ...prev,
        { id: crypto.randomUUID(), role: 'assistant', content: 'Here is your preview below. You can iterate further anytime.' },
      ])
    } catch (err) {
      setError('Failed to generate preview. Please try again.')
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <div className="min-h-[70vh] flex flex-col">
      <div className="flex-1 overflow-y-auto px-4 pt-24 pb-4">
        <div className="max-w-2xl mx-auto space-y-4">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.type === 'socials' ? (
                <SocialsCard onContinue={handleSocialsContinue} />
              ) : m.type === 'dex' ? (
                <DexCard onContinue={handleDexContinue} />
              ) : m.type === 'confirm' ? (
                <ConfirmCard data={data} onGenerate={generatePreview} generating={isGenerating} />
              ) : (
                <div className={`${m.role === 'user' ? 'bg-cyan-500 text-white' : 'glass-card text-gray-200'} px-4 py-3 rounded-2xl max-w-[85%]`}> 
                  <div className="whitespace-pre-wrap leading-relaxed text-sm">{m.content}</div>
                </div>
              )}
            </div>
          ))}

          {error && (
            <div className="glass-card border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm">{error}</div>
          )}

          {previewHtml && (
            <div className="mt-4 border border-cyan-500/20 rounded-xl overflow-hidden shadow-lg">
              <div className="flex items-center justify-between p-3 bg-gray-100 border-b border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Layers className="w-4 h-4" />
                  Live Preview
                </div>
              </div>
              <div className="h-[60vh] bg-white">
                <iframe srcDoc={previewHtml} title="Preview" className="w-full h-full border-0" sandbox="allow-scripts allow-same-origin" />
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>
      </div>

      {(currentStep === 'tokenName' || currentStep === 'tokenSymbol' || currentStep === 'contractAddress') && (
        <div className="border-t border-gray-800 bg-gray-900/70 backdrop-blur-sm">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
            <input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit() }}
              placeholder={currentStep === 'tokenName' ? 'Enter token name' : currentStep === 'tokenSymbol' ? 'Enter token symbol (e.g., MOON)' : 'Paste contract address'}
              className="flex-1 input-field bg-gray-800/70 border-gray-700"
            />
            <button onClick={handleSubmit} disabled={!canSubmit} className="btn-primary px-4 py-2">
              {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function SocialsCard({ onContinue }) {
  const [twitterLink, setTwitterLink] = useState('')
  const [telegramLink, setTelegramLink] = useState('')

  return (
    <div className="glass-card p-4 max-w-[85%]">
      <div className="flex items-center gap-2 mb-3">
        <Globe className="w-4 h-4 text-cyan-400" />
        <div className="text-white font-medium">Add socials (optional)</div>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Twitter className="w-4 h-4 text-blue-400" />
          <input value={twitterLink} onChange={(e) => setTwitterLink(e.target.value)} placeholder="Twitter URL" className="input-field flex-1" />
        </div>
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-cyan-400" />
          <input value={telegramLink} onChange={(e) => setTelegramLink(e.target.value)} placeholder="Telegram URL" className="input-field flex-1" />
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={() => onContinue({ twitter: '', telegram: '' })} className="btn-secondary text-sm">Skip</button>
          <button onClick={() => onContinue({ twitter: twitterLink, telegram: telegramLink })} className="btn-primary text-sm">
            Continue <ChevronRight className="w-4 h-4 ml-1 inline" />
          </button>
        </div>
      </div>
    </div>
  )
}

function DexCard({ onContinue }) {
  const [dex, setDex] = useState({ pumpswap: false, pancakeswap: false, raydium: false, jupiter: false, dexscreener: false })
  const [open, setOpen] = useState(true)

  const toggle = (k) => setDex(prev => ({ ...prev, [k]: !prev[k] }))

  return (
    <div className="glass-card p-4 max-w-[85%]">
      <button className="w-full flex items-center justify-between text-left" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-yellow-400" />
          <div className="text-white font-medium">Select DEX platforms</div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="grid grid-cols-2 gap-3 mt-3">
          {Object.keys(dex).map((k) => (
            <label key={k} className={`glass-card p-3 flex items-center gap-2 cursor-pointer ${dex[k] ? 'border-cyan-400/40 bg-cyan-500/5' : ''}`}>
              <input type="checkbox" checked={dex[k]} onChange={() => toggle(k)} className="w-4 h-4 text-cyan-500" />
              <span className="text-sm text-gray-200 capitalize">{k}</span>
            </label>
          ))}
        </div>
      )}
      <div className="flex items-center justify-end gap-2 mt-3">
        <button onClick={() => onContinue(dex)} className="btn-primary text-sm">
          Continue <ChevronRight className="w-4 h-4 ml-1 inline" />
        </button>
      </div>
    </div>
  )
}

function ConfirmCard({ data, onGenerate, generating }) {
  return (
    <div className="glass-card p-4 max-w-[85%]">
      <div className="text-white font-medium mb-3">Confirm details</div>
      <div className="text-sm text-gray-300 space-y-1">
        <div><span className="text-gray-400">Token:</span> {data.tokenName} (${data.tokenSymbol})</div>
        <div><span className="text-gray-400">Contract:</span> {data.contractAddress}</div>
        {(data.socialLinks.twitter || data.socialLinks.telegram) && (
          <div>
            <span className="text-gray-400">Socials:</span> {data.socialLinks.twitter || '—'} {data.socialLinks.telegram ? `• ${data.socialLinks.telegram}` : ''}
          </div>
        )}
        <div>
          <span className="text-gray-400">DEX:</span> {Object.entries(data.dexPlatforms).filter(([,v]) => v).map(([k]) => k).join(', ') || 'None'}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <button onClick={onGenerate} disabled={generating} className="btn-primary">
          {generating ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>) : (<><Check className="w-4 h-4 mr-2" /> Generate Preview</>)}
        </button>
      </div>
    </div>
  )
}

export default SiteGen2 